import React, { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../api/axios";
import { runBackgroundRequest } from "../utils/backgroundRequestQueue";

export const MutualUnreadContext = createContext(null);

const clampInt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
};

const normalizeUserIds = (value) => {
  if (!Array.isArray(value)) return [];
  return [...new Set(
    value
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item > 0)
  )];
};

export const formatUnreadBadge = (count) => {
  const n = clampInt(count);
  if (n <= 0) return "";
  if (n > 999) return "999+";
  return String(n);
};

export const MutualUnreadProvider = ({
  children,
  enabled = true,
  intervalMs = 60_000,
}) => {
  const { data, refetch } = useQuery({
    queryKey: ["mutual_unread_summary"],
    enabled: !!enabled,
    queryFn: async () => {
      const res = await runBackgroundRequest(() => (
        api.get("/ratings/mutual-unread-summary/", {
          meta: { suppressGlobalError: true },
        })
      ));
      return res?.data || { unread_count: 0, total_mutual: 0, has_blocking_pending: false };
    },
    refetchInterval: intervalMs,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const markSeen = useCallback(async (userIds) => {
    const payload = Array.isArray(userIds)
      ? { user_ids: normalizeUserIds(userIds) }
      : {};
    await api.post("/ratings/mutual-unread-mark-seen/", payload);
    await refetch();
  }, [refetch]);

  const value = useMemo(() => {
    const unreadCount = clampInt(data?.unread_count);
    const unreadUserIds = normalizeUserIds(data?.unread_user_ids);
    const totalMutual = clampInt(data?.total_mutual);
    const hasBlockingPending = Boolean(data?.has_blocking_pending);

    return {
      unreadCount,
      unreadUserIds,
      totalMutual,
      hasBlockingPending,
      formatUnreadBadge,
      refetch,
      markSeen,
    };
  }, [data, markSeen, refetch]);

  return (
    <MutualUnreadContext.Provider value={value}>
      {children}
    </MutualUnreadContext.Provider>
  );
};

export const useMutualUnread = () => {
  const ctx = useContext(MutualUnreadContext);
  return (
    ctx || {
      unreadCount: 0,
      unreadUserIds: [],
      totalMutual: 0,
      hasBlockingPending: false,
      formatUnreadBadge,
      refetch: async () => {},
      markSeen: async () => {},
    }
  );
};
